import { useCallback, useState } from 'react';
import { TFunction } from 'i18next';
import {
  useSsoIntegrationGenerateCodeQuery,
  useSsoIntegrationGenerateCodeLazyQuery,
} from 'graphql-common';
import { FormRef } from '@lib/components/ReactHookForm/FormContainer';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { SsoIntegrationFieldNames } from './constants';

type UseGeneratedCodeArgs = {
  formRef: React.RefObject<FormRef | undefined>;
  skip: boolean;
  t: TFunction;
};

export default function useGeneratedCode({
  formRef,
  skip,
  t,
}: UseGeneratedCodeArgs) {
  const {
    data: generatedCodeData,
    loading: codeLoading,
    error: codeError,
  } = useSsoIntegrationGenerateCodeQuery({
    skip,
    fetchPolicy: 'no-cache',
  });

  const [generateCodeLazy] = useSsoIntegrationGenerateCodeLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [isRegenerating, setIsRegenerating] = useState(false);

  const initialCode = generatedCodeData?.data?.code ?? '';
  const codeFirstLoading = !skip && codeLoading;

  const regenerateCode = useCallback(async () => {
    setIsRegenerating(true);
    try {
      const result = await generateCodeLazy();
      if (result.error) {
        showGraphQLErrors(result.error, t);
        return;
      }
      const code = result.data?.data?.code;
      if (code && formRef.current?.setValue) {
        formRef.current.setValue(SsoIntegrationFieldNames.Code, code);
      }
    } catch (error) {
      showGraphQLErrors(error, t);
    } finally {
      setIsRegenerating(false);
    }
  }, [generateCodeLazy, formRef, t]);

  return {
    regenerateCode,
    isRegenerating,
    initialCode,
    codeFirstLoading,
    codeError,
  };
}
